import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { s, authHeaders, Toast } from './listPageStyles';
import { StatusBadge } from './ui';
import { usePageMeta } from '../hooks/usePageMeta';
import { usePageActions } from '../contexts/PermissionContext';
import './CustomerDetailPage.css';

const STATUS_META = {
  0: { label: 'Aktif', tone: 'ok' },
  1: { label: 'Pasif', tone: 'neutral' },
  2: { label: 'Beklemede', tone: 'wait' },
  // 3 = Potansiyel: "İşlem Görmeyen Müşteriler" zamanlanmış görevinin (menü 9608) yazdığı durum.
  // Müşteri PASİF DEĞİLDİR — rutta kalır ve hareket görünce otomatik eski durumuna döner.
  3: { label: 'Potansiyel', tone: 'wait' },
};

// #129 E-Belge Mükellef Tipi — GİB/servis sağlayıcıdan ileride güncellenebilir.
const EDOCUMENT_TYPES = [
  { value: '', label: 'Belirtilmemiş' },
  { value: 'EINVOICE', label: 'E-Fatura' },
  { value: 'EINVOICE_EDISPATCH', label: 'E-Fatura + E-İrsaliye' },
  { value: 'EARCHIVE', label: 'E-Arşiv' },
];
const edocumentLabel = (v) => EDOCUMENT_TYPES.find((t) => t.value === (v || ''))?.label || '—';

// #129 Varsayılan Sevkiyat Belge Tipi — müşteri kartı alanı.
const SHIPMENT_DOC_TYPES = [
  { value: 'IRSALIYE', label: 'İrsaliye' },
  { value: 'FATURA', label: 'Fatura' },
];
const shipmentDocLabel = (v) => SHIPMENT_DOC_TYPES.find((o) => o.value === v)?.label || '-';

// Mobil OperationsSlider.kt sırasına paralel
const TABS = [
  { value: 'overview', label: 'Genel Bakış', icon: 'eye' },
  { value: 'collection', label: 'Tahsilat', icon: 'banknote' },
  { value: 'promo', label: 'Promo', icon: 'gift' },
  { value: 'survey', label: 'Anket', icon: 'clip' },
  { value: 'cooler', label: 'Soğutucu', icon: 'snow' },
  { value: 'standCount', label: 'Stand Sayım', icon: 'boxes' },
  { value: 'agreement', label: 'Anlaşma', icon: 'filesign' },
  { value: 'certificatedReturn', label: 'Belgeli İade', icon: 'filereturn' },
  { value: 'depositCount', label: 'Depozito', icon: 'bottle' },
];

const PAYMENT_CHANNELS = [
  { value: 'CASH', label: 'Nakit', icon: '💵', color: '#2E7D32' },
  { value: 'CARD', label: 'Kredi Kartı', icon: '💳', color: '#1565C0' },
  { value: 'CHECK', label: 'Çek', icon: '📝', color: '#EF6C00' },
  { value: 'PROMISSORY_NOTE', label: 'Senet', icon: '📄', color: '#6A1B9A' },
  { value: 'BANK_TRANSFER', label: 'Havale / EFT', icon: '🏦', color: '#00838F' },
];

const QUICK_AMOUNTS = [100, 500, 1000, 5000];

function formatTRY(value) {
  return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', minimumFractionDigits: 2 }).format(value || 0);
}

function formatTRYShort(value) {
  return `₺${new Intl.NumberFormat('tr-TR', { maximumFractionDigits: 0 }).format(value || 0)}`;
}

// Miktar: tam sayıysa ondalıksız, değilse en fazla 2 hane.
function formatQty(value) {
  return new Intl.NumberFormat('tr-TR', { maximumFractionDigits: 2 }).format(value || 0);
}

function unwrap(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.Data)) return payload.Data;
  if (Array.isArray(payload?.value)) return payload.value;
  return [];
}

function unwrapEntity(payload) {
  if (!payload) return null;
  return payload.data ?? payload.Data ?? payload.value ?? payload;
}

// /api/campaigns?take=N → { data: { items: [...] } } şeklinde döner.
function unwrapCampaigns(payload) {
  const items = payload?.data?.items ?? payload?.Data?.items ?? payload?.items;
  if (Array.isArray(items)) return items;
  return unwrap(payload);
}

const pick = (obj, ...keys) => {
  for (const k of keys) {
    if (obj && obj[k] !== undefined && obj[k] !== null) return obj[k];
  }
  return undefined;
};

const csvHas = (csv, value) => {
  if (!csv || !value) return false;
  return String(csv).split(',').map((x) => x.trim().toLowerCase()).includes(String(value).trim().toLowerCase());
};

/**
 * Bir kampanyanın bu müşteriye uygulanıp uygulanmadığını istemcide çözer
 * (backend'de müşteri-bazlı kampanya endpoint'i yok). DYNAMIC (özelleştirilmiş grup
 * üyeliği) bu turda kapsam dışıdır — değerlendirilmez.
 */
function campaignAppliesToCustomer(c, customer) {
  if (!c || !customer) return false;
  const isActive = pick(c, 'isActive', 'IsActive');
  if (isActive === false) return false;
  const excluded = pick(c, 'excludedCustomerIdsCsv', 'ExcludedCustomerIdsCsv');
  if (csvHas(excluded, customer.id)) return false;
  const type = String(pick(c, 'customerTargetType', 'CustomerTargetType') || 'ALL').toUpperCase();
  switch (type) {
    case 'ALL': return true;
    case 'CUSTOMER': return csvHas(pick(c, 'targetCustomerIdsCsv', 'TargetCustomerIdsCsv'), customer.id);
    case 'GROUP': return csvHas(pick(c, 'targetCustomerGroupIdsCsv', 'TargetCustomerGroupIdsCsv'), customer.groupId);
    case 'SUBGROUP': return csvHas(pick(c, 'targetCustomerSubGroupIdsCsv', 'TargetCustomerSubGroupIdsCsv'), customer.subGroupId);
    default: return false; // DYNAMIC ve bilinmeyenler kapsam dışı
  }
}

// status 4 = Tamamlandı (OrderHeadersPage STATUS_OPTIONS ile uyumlu)
const ORDER_STATUS_COMPLETED = 4;
const shortOrderNo = (id) => (id ? `#${String(id).slice(0, 8).toUpperCase()}` : '—');
function orderDateLabel(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return new Intl.DateTimeFormat('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' }).format(d);
}

// Tek renk (currentColor) çizgi ikonlar — tasarım paritesi (emoji yerine).
const ICON_PATHS = {
  user: <><circle cx="12" cy="8" r="3.2" /><path d="M5.5 20a6.5 6.5 0 0 1 13 0" /></>,
  phone: <path d="M6.6 3h3l1.5 4-2 1.4a11 11 0 0 0 5 5l1.4-2 4 1.5v3a2 2 0 0 1-2.2 2A16 16 0 0 1 4.6 5.2 2 2 0 0 1 6.6 3z" />,
  mail: <><rect x="3" y="5" width="18" height="14" rx="2" /><path d="M4 7l8 6 8-6" /></>,
  tag: <><path d="M3 12l9-9 9 9-9 9-9-9z" /><circle cx="9.5" cy="9.5" r="1.3" /></>,
  users: <><circle cx="9" cy="8" r="3" /><path d="M3 19a6 6 0 0 1 12 0" /><path d="M16 6a3 3 0 0 1 0 6" /><path d="M18 19a5 5 0 0 0-3-4.6" /></>,
  folder: <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7z" />,
  truck: <><path d="M3 7h11v8H3z" /><path d="M14 10h4l3 3v2h-7z" /><circle cx="7" cy="18" r="1.8" /><circle cx="17" cy="18" r="1.8" /></>,
  building: <><path d="M5 21V4a1 1 0 0 1 1-1h7a1 1 0 0 1 1 1v17" /><path d="M14 9h4a1 1 0 0 1 1 1v11" /><path d="M8 7h3M8 11h3M8 15h3" /></>,
  pin: <><path d="M12 21s7-6.3 7-11a7 7 0 1 0-14 0c0 4.7 7 11 7 11z" /><circle cx="12" cy="10" r="2.4" /></>,
  receipt: <><path d="M6 3h12v18l-2.5-1.6L13 21l-2.5-1.6L8 21l-2-1.6V3z" /><path d="M9 8h6M9 12h6" /></>,
  bank: <path d="M4 10h16M5 10l7-5 7 5M6 10v8M10 10v8M14 10v8M18 10v8M4 21h16" />,
  trash: <path d="M4 7h16M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2M6 7l1 13a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1l1-13" />,
  pencil: <><path d="M4 20h4l10-10-4-4L4 16v4z" /><path d="M13.5 6.5l4 4" /></>,
  trendingUp: <><polyline points="3 17 9 11 13 15 21 7" /><polyline points="15 7 21 7 21 13" /></>,
  clock: <><circle cx="12" cy="12" r="9" /><polyline points="12 7 12 12 16 14" /></>,
  box: <><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z" /><polyline points="3.3 7 12 12 20.7 7" /><line x1="12" y1="22" x2="12" y2="12" /></>,
  card: <><rect x="3" y="6" width="18" height="12" rx="2" /><path d="M3 10h18" /><path d="M7 15h3" /></>,
  snow: <><line x1="12" y1="2" x2="12" y2="22" /><line x1="2" y1="12" x2="22" y2="12" /><line x1="4.5" y1="4.5" x2="19.5" y2="19.5" /><line x1="19.5" y1="4.5" x2="4.5" y2="19.5" /></>,
  bottle: <><path d="M10 2h4v2.5l1 2V21a1 1 0 0 1-1 1H10a1 1 0 0 1-1-1V6.5l1-2Z" /><line x1="9" y1="11" x2="15" y2="11" /></>,
  wallet: <><path d="M21 12V7H5a2 2 0 0 1 0-4h14v4" /><path d="M3 5v14a2 2 0 0 0 2 2h16v-5" /><path d="M18 12a2 2 0 0 0 0 4h4v-4Z" /></>,
  trending: <><polyline points="22 7 13.5 15.5 8.5 10.5 2 17" /><polyline points="16 7 22 7 22 13" /></>,
  spark: <path d="M12 2l1.7 6.3L20 10l-6.3 1.7L12 18l-1.7-6.3L4 10l6.3-1.7Z" />,
  flame: <path d="M12 2s5 5.5 5 11a5 5 0 0 1-10 0c0-2 1-3.5 1-3.5S9 12 11 12c0-3 1-7 1-10Z" />,
  clip: <><rect x="5" y="4" width="14" height="17" rx="2" /><path d="M9 4a3 3 0 0 1 6 0" /><line x1="9" y1="11" x2="15" y2="11" /><line x1="9" y1="15" x2="13" y2="15" /></>,
  banknote: <><rect x="2" y="6" width="20" height="12" rx="2" /><circle cx="12" cy="12" r="2.5" /><line x1="6" y1="12" x2="6.01" y2="12" /><line x1="18" y1="12" x2="18.01" y2="12" /></>,
  refresh: <><polyline points="21 3 21 9 15 9" /><path d="M3 12a9 9 0 0 1 15-6.7L21 9" /><polyline points="3 21 3 15 9 15" /><path d="M21 12a9 9 0 0 1-15 6.7L3 15" /></>,
  plus: <><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></>,
  chevR: <polyline points="9 18 15 12 9 6" />,
  eye: <><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" /><circle cx="12" cy="12" r="3" /></>,
  gift: <><rect x="3" y="8" width="18" height="4" rx="1" /><path d="M5 12v8a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-8" /><line x1="12" y1="8" x2="12" y2="21" /><path d="M12 8S10.5 3 7.5 4.5 9 8 12 8Z" /><path d="M12 8s1.5-5 4.5-3.5S15 8 12 8Z" /></>,
  boxes: <><rect x="3" y="9" width="8" height="8" rx="1" /><rect x="13" y="9" width="8" height="8" rx="1" /><rect x="8" y="2.5" width="8" height="6" rx="1" /></>,
  filesign: <><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h7" /><polyline points="14 2 14 8 20 8" /><path d="M16 18s1-2 2.5-2 .8 2 2.2 2" /></>,
  filereturn: <><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z" /><polyline points="14 2 14 8 20 8" /><polyline points="12 12 9.5 14.5 12 17" /><path d="M9.5 14.5H14a1.5 1.5 0 0 1 0 3" /></>,
};

// Mockup pillTone: Yüksek/Kritik → amber, Aktif → green, diğer → navy.
function pillTone(v) {
  return v === 'Yüksek' || v === 'Kritik' ? 'amber' : v === 'Aktif' ? 'green' : 'navy';
}

function CdIcon({ name, size = 18, stroke = 2, style }) {
  const path = ICON_PATHS[name];
  if (!path) return null;
  const filled = name === 'spark';
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={filled ? 'currentColor' : 'none'}
      stroke={filled ? 'none' : 'currentColor'} strokeWidth={stroke} strokeLinecap="round" strokeLinejoin="round" style={style} aria-hidden="true">
      {path}
    </svg>
  );
}

/**
 * Mobile parity: local-first tahsilat.
 *   - balance.total = customer.currentBalance (Customer master'dan, mobil ile aynı)
 *   - recent     = localStorage'dan okunur (mobil Room DB karşılığı)
 *   - submit     = localStorage'a `syncStatus: 'pending'` ile yazılır, anında success döner
 *                  (mobil SyncQueue karşılığı; backend Payment apply branch hazır olunca push'a bağlanacak)
 */
const STORAGE_KEYS = {
  payments: 'fronta:payments:',
  surveys: 'fronta:surveys:',
  coolerFaults: 'fronta:coolerFaults:',
  standCounts: 'fronta:standCounts:',
  returns: 'fronta:returns:',
  deposits: 'fronta:deposits:',
};

function loadLocal(prefix, customerId) {
  if (!customerId) return [];
  try {
    const raw = localStorage.getItem(prefix + customerId);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function saveLocal(prefix, customerId, records) {
  if (!customerId) return;
  try {
    localStorage.setItem(prefix + customerId, JSON.stringify(records));
  } catch {
    /* quota / private mode */
  }
}

// Tahsilat için kısayollar (backward compatibility)
function loadLocalPayments(customerId) {
  return loadLocal(STORAGE_KEYS.payments, customerId);
}

function saveLocalPayments(customerId, records) {
  saveLocal(STORAGE_KEYS.payments, customerId, records);
}

function relativeLabel(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  const yesterday = new Date(now); yesterday.setDate(now.getDate() - 1);
  const isYesterday = d.toDateString() === yesterday.toDateString();
  const hhmm = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  if (sameDay) return `Bugün ${hhmm}`;
  if (isYesterday) return `Dün ${hhmm}`;
  return new Intl.DateTimeFormat('tr-TR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }).format(d);
}

// ========== Operation enum'ları (mobile model'lerinin birebir karşılığı) ==========
const SURVEY_CATEGORIES = [
  { value: 'OPERATIONS', label: 'Operasyon' },
  { value: 'PRODUCT', label: 'Ürün' },
  { value: 'SERVICE', label: 'Servis' },
  { value: 'PROMOTION', label: 'Promosyon' },
  { value: 'GENERAL', label: 'Genel' },
];

const COOLER_REASONS = [
  { value: 'COMPRESSOR', label: 'Kompresör' },
  { value: 'DOOR', label: 'Kapı' },
  { value: 'LEAK', label: 'Sızıntı' },
  { value: 'ELECTRICAL', label: 'Elektrik' },
  { value: 'NOT_COOLING', label: 'Soğutmuyor' },
  { value: 'OTHER', label: 'Diğer' },
];

const COOLER_URGENCY = [
  { value: 'LOW', label: 'Düşük' },
  { value: 'NORMAL', label: 'Normal' },
  { value: 'HIGH', label: 'Yüksek' },
  { value: 'CRITICAL', label: 'Kritik' },
];

const AGREEMENT_TYPES = [
  { value: 'DSA', label: 'DSA' },
  { value: 'SPACEMAN', label: 'Spaceman' },
  { value: 'CONTRACT', label: 'Sözleşme' },
];

const RETURN_TYPES = [
  { value: 'CUSTOMER_ORDER', label: 'Müşteri Sipariş' },
  { value: 'CUSTOMER_RECEIPT', label: 'Müşteri İrsaliye' },
  { value: 'CUSTOMER_INVOICE', label: 'Müşteri Fatura' },
  { value: 'VEHICLE_TO_DEALER', label: 'Araç → Bayi' },
];

const DEPOSIT_TYPES = [
  { value: 'BOTTLE_GLASS', label: 'Cam Şişe', icon: '🍾' },
  { value: 'BOTTLE_PET', label: 'PET Şişe', icon: '🧴' },
  { value: 'CRATE', label: 'Kasa', icon: '📦' },
  { value: 'CO2_TANK', label: 'CO₂ Tüpü', icon: '🛢' },
];

const PRODUCT_DEMO_LIST = [
  { code: 'COLA-330', name: 'Fronta Cola 330ml' },
  { code: 'COLA-1L', name: 'Fronta Cola 1L' },
  { code: 'GZ-LMN', name: 'Gazoz Limon 250ml' },
  { code: 'WTR-50', name: 'Su 500ml' },
  { code: 'IT-200', name: 'Ice Tea Şeftali 200ml' },
];

// Genel Bakış'ta AKILLI ÖNERİLER — LLM YOK. Müşterinin gerçek verisinden
// (bakiye, top ürün/kampanya, sipariş güncelliği) kural/query ile hesaplanan
// eyleme dönük öneriler. Mockup DEMO_UPSELL'in yerine geçer.
function SmartInsights({ customer, orders = [], topSkus = [], topCampaigns = [] }) {
  const insights = [];
  const balance = Number(customer.currentBalance ?? 0);

  // 1) Bakiye
  if (balance > 0) {
    insights.push({ icon: 'wallet', tone: 'warn', title: 'Açık bakiye', text: `${formatTRY(balance)} tahsil edilecek — tahsilat önceliği` });
  } else if (balance < 0) {
    insights.push({ icon: 'wallet', tone: 'ok', title: 'Müşteri lehine bakiye', text: `${formatTRY(Math.abs(balance))} alacaklı` });
  } else {
    insights.push({ icon: 'wallet', tone: 'ok', title: 'Bakiye temiz', text: 'Açık bakiye yok' });
  }

  // 2) En çok alınan ürün (reorder/çapraz satış sinyali)
  if (topSkus.length) {
    const t = topSkus[0];
    insights.push({ icon: 'box', tone: 'info', title: 'En çok alınan ürün', text: `${pick(t, 'name', 'Name') || '-'} · ${formatQty(Number(pick(t, 'quantity', 'Quantity') || 0))} adet — reorder takibi` });
  }

  // 3) Kampanya kullanımı
  if (topCampaigns.length) {
    const totalDisc = topCampaigns.reduce((sum, c) => sum + (Number(pick(c, 'totalDiscount', 'TotalDiscount')) || 0), 0);
    insights.push({ icon: 'spark', tone: 'info', title: 'Kampanya etkisi', text: `${topCampaigns.length} kampanya · toplam ${formatTRY(totalDisc)} iskonto` });
  } else {
    insights.push({ icon: 'spark', tone: 'warn', title: 'Kampanya fırsatı', text: 'Bu müşteri kampanya kullanmamış — uygun kampanya öner' });
  }

  // 4) Sipariş güncelliği
  if (!orders.length) {
    insights.push({ icon: 'trending', tone: 'warn', title: 'İlk sipariş fırsatı', text: 'Henüz sipariş kaydı yok' });
  } else {
    const last = orders.find((o) => pick(o, 'documentDate', 'DocumentDate')) || orders[0];
    const raw = pick(last, 'documentDate', 'DocumentDate');
    const d = raw ? new Date(raw) : null;
    const days = d && !isNaN(d.getTime()) ? Math.floor((Date.now() - d.getTime()) / 86400000) : null;
    if (days != null && days > 30) {
      insights.push({ icon: 'clock', tone: 'warn', title: 'Sipariş durgun', text: `Son sipariş ${days} gün önce — ziyaret zamanı` });
    } else if (days != null) {
      insights.push({ icon: 'trending', tone: 'ok', title: 'Aktif müşteri', text: `Son sipariş ${days} gün önce` });
    }
  }

  return (
    <div className="ck-ai">
      <div className="ck-ai-h"><CdIcon name="flame" size={16} /> Akıllı Öneriler <span className="ck-ai-badge">Otomatik</span></div>
      <div className="ck-smart-list">
        {insights.map((it, i) => (
          <div className={`ck-smart ck-smart--${it.tone}`} key={i}>
            <span className="ck-smart-ic"><CdIcon name={it.icon} size={16} /></span>
            <div className="ck-smart-tt"><b>{it.title}</b><span>{it.text}</span></div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Sipariş Trendi — son 6 ayın aylık sipariş tutarı (orders'tan hesaplanır) + özet.
function OrderTrend({ orders = [] }) {
  const now = new Date();
  const months = [];
  for (let i = 5; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    months.push({ key: `${d.getFullYear()}-${d.getMonth()}`, label: d.toLocaleDateString('tr-TR', { month: 'short' }), total: 0, count: 0 });
  }
  const idx = {};
  months.forEach((m, i) => { idx[m.key] = i; });
  let grand = 0;
  let cnt = 0;
  (orders || []).forEach((o) => {
    const amt = Number(pick(o, 'netAmount', 'NetAmount') || 0);
    grand += amt; cnt += 1;
    const raw = pick(o, 'documentDate', 'DocumentDate');
    if (!raw) return;
    const d = new Date(raw);
    if (isNaN(d.getTime())) return;
    const k = `${d.getFullYear()}-${d.getMonth()}`;
    if (k in idx) { months[idx[k]].total += amt; months[idx[k]].count += 1; }
  });
  const max = Math.max(1, ...months.map((m) => m.total));
  const avg = cnt ? grand / cnt : 0;

  return (
    <div className="ck-panel">
      <div className="ck-panel-h"><h3><CdIcon name="trending" size={18} /> Sipariş Trendi</h3><span>Son 6 ay</span></div>
      <div className="ck-trend">
        {months.map((m) => (
          <div className="ck-trend-col" key={m.key} title={`${m.label}: ${formatTRY(m.total)} · ${m.count} sipariş`}>
            <div className="ck-trend-barwrap"><div className="ck-trend-bar" style={{ height: `${Math.max(3, Math.round((m.total / max) * 100))}%` }} /></div>
            <span className="ck-trend-lbl">{m.label}</span>
          </div>
        ))}
      </div>
      <div className="ck-trend-stats">
        <div><b>{cnt}</b><span>Toplam sipariş</span></div>
        <div><b>{formatTRY(grand)}</b><span>Toplam tutar</span></div>
        <div><b>{formatTRY(avg)}</b><span>Ort. sipariş</span></div>
      </div>
    </div>
  );
}

// AKILLI ÖNERİ 1 — Ziyaret Önerisi (LLM YOK, kural/sorgu). Bakiye + sipariş
// durgunluğu + ziyaret başarı oranından öncelik ve aksiyon hesaplar.
function VisitSuggestion({ customer, orders = [], m }) {
  const balance = Number(customer.currentBalance ?? 0);
  const list = orders || [];
  const lastRaw = list.length ? pick(list[0], 'documentDate', 'DocumentDate') : null;
  const dd = lastRaw ? new Date(lastRaw) : null;
  const days = dd && !isNaN(dd.getTime()) ? Math.floor((Date.now() - dd.getTime()) / 86400000) : null;
  const hitRate = m ? Number(m.hitRate ?? m.HitRate ?? 0) : null;
  let priority = 'low';
  let reason = 'Rutin ziyaret planı yeterli.';
  let action = 'Planlı ziyaret';
  if (balance > 0 && (days == null || days > 20)) { priority = 'high'; reason = `${formatTRY(balance)} açık bakiye${days != null ? ` · son sipariş ${days} gün önce` : ''}.`; action = 'Tahsilat + sipariş ziyareti'; }
  else if (days != null && days > 30) { priority = 'high'; reason = `Sipariş durgun — son sipariş ${days} gün önce.`; action = 'Sipariş ziyareti'; }
  else if (balance > 0) { priority = 'medium'; reason = `${formatTRY(balance)} açık bakiye tahsil edilebilir.`; action = 'Tahsilat ziyareti'; }
  else if (days != null && days <= 7) { priority = 'low'; reason = `Son sipariş ${days} gün önce — yakında ilgilenildi.`; action = 'Takip ziyareti'; }
  const tone = priority === 'high' ? 'r' : priority === 'medium' ? 'a' : 'g';
  const plabel = priority === 'high' ? 'Yüksek' : priority === 'medium' ? 'Orta' : 'Düşük';
  return (
    <div className="ck-panel">
      <div className="ck-ai-h"><CdIcon name="clock" size={16} /> Ziyaret Önerisi <span className={`ck-ai-pri ck-ai-pri--${tone}`}>{plabel} öncelik</span></div>
      <div className="ck-suggest">
        <div className="ck-suggest-main"><b>{action}</b><span>{reason}</span></div>
        {hitRate != null && hitRate > 0 && <div className="ck-suggest-note">Bu müşteride ziyaret başarı oranı %{Math.round(hitRate)}</div>}
      </div>
    </div>
  );
}

// AKILLI ÖNERİ 2 — Satılabilecek Ürünler (sorgu tabanlı; sipariş geçmişinden
// reorder/mevsimsel/çapraz satış skoruyla: GET /api/orders/suggestions/history).
function ProductSuggestion({ customer }) {
  const [items, setItems] = useState(null);
  useEffect(() => {
    let alive = true;
    fetch(`/api/orders/suggestions/history?customerId=${encodeURIComponent(customer.id)}`, { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : null))
      .then((json) => { if (!alive) return; const rows = json?.data ?? json?.Data ?? json?.value ?? (Array.isArray(json) ? json : []); setItems(Array.isArray(rows) ? rows : []); })
      .catch(() => { if (alive) setItems([]); });
    return () => { alive = false; };
  }, [customer.id]);
  return (
    <div className="ck-panel">
      <div className="ck-ai-h"><CdIcon name="box" size={16} /> Satılabilecek Ürünler <span className="ck-ai-badge">Otomatik</span></div>
      {items == null ? (
        <div className="ck-suggest-note" style={{ padding: '4px 0' }}>Öneriler hazırlanıyor…</div>
      ) : items.length === 0 ? (
        <div className="ck-suggest-note" style={{ padding: '4px 0' }}>Yeterli sipariş geçmişi yok — öneri üretilemedi.</div>
      ) : (
        <div className="ck-prodsug">
          {items.slice(0, 5).map((p, i) => (
            <div className="ck-prodsug-row" key={i}>
              <span className="ck-prodsug-ic"><CdIcon name="box" size={14} /></span>
              <div className="ck-prodsug-main"><b>{p.name ?? p.Name ?? '-'}</b>{(p.reason ?? p.Reason) && <span>{p.reason ?? p.Reason}</span>}</div>
              {(p.qty ?? p.Qty ?? p.suggestedQty) != null && <span className="ck-prodsug-qty">{p.qty ?? p.Qty ?? p.suggestedQty} adet</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Aktivite feed tip → ikon/renk (ORDER/PAYMENT/SURVEY/NOSALE=satışsız ziyaret).
const ACTIVITY_META = {
  ORDER: { icon: 'box', tone: 'blue', label: 'Sipariş' },
  PAYMENT: { icon: 'banknote', tone: 'green', label: 'Tahsilat' },
  SURVEY: { icon: 'clip', tone: 'purple', label: 'Anket' },
  NOSALE: { icon: 'clock', tone: 'amber', label: 'Ziyaret (satış yok)' },
};

// Müşteri Sağlık Skoru — sorgu/kural tabanlı bileşik skor (LLM değil). Bakiye riski +
// sipariş güncelliği + ziyaret başarısı + kampanya katılımından 0-100 hesaplar.
// span-1 companion: "Sipariş Geçmişi" span-2 panelinin yanındaki boşluğu doldurur.
function HealthScoreCard({ customer, orders = [], m, topCampaigns = [] }) {
  const balance = Number(customer.currentBalance ?? 0);
  const lastRaw = orders.length ? pick(orders[0], 'documentDate', 'DocumentDate') : null;
  const dd = lastRaw ? new Date(lastRaw) : null;
  const days = dd && !isNaN(dd.getTime()) ? Math.floor((Date.now() - dd.getTime()) / 86400000) : null;
  const hit = Number(m?.hitRate ?? m?.HitRate ?? 0);
  const fBal = balance <= 0 ? 100 : balance < 25000 ? 70 : balance < 75000 ? 45 : 20;
  const fRec = days == null ? 40 : days <= 7 ? 100 : days <= 15 ? 80 : days <= 30 ? 55 : 25;
  const fHit = m ? Math.min(100, Math.round(hit)) : 50;
  const fCamp = topCampaigns.length ? 100 : 40;
  const score = Math.round(fBal * 0.35 + fRec * 0.30 + fHit * 0.20 + fCamp * 0.15);
  const tone = score >= 70 ? 'var(--ck-green)' : score >= 45 ? 'var(--ck-amber)' : 'var(--ck-red)';
  const label = score >= 70 ? 'Sağlıklı' : score >= 45 ? 'İzlemede' : 'Riskli';
  const barTone = (v) => (v >= 70 ? 'var(--ck-green)' : v >= 45 ? 'var(--ck-amber)' : 'var(--ck-red)');
  const factors = [
    { k: 'Bakiye durumu', v: fBal },
    { k: 'Sipariş güncelliği', v: fRec },
    { k: 'Ziyaret başarısı', v: fHit },
    { k: 'Kampanya katılımı', v: fCamp },
  ];
  return (
    <div className="ck-panel ck-health">
      <div className="ck-panel-h"><h3><CdIcon name="spark" size={16} /> Müşteri Sağlık Skoru</h3></div>
      <div className="ck-health-top">
        <div className="ck-donut" style={{ background: `conic-gradient(${tone} ${score}%, var(--ck-line) 0)` }}><span>{score}</span></div>
        <span className="ck-health-badge" style={{ color: tone }}>{label}</span>
      </div>
      <div className="ck-health-factors">
        {factors.map((f, i) => (
          <div className="ck-hf" key={i}>
            <div className="ck-hf-top"><span>{f.k}</span><b>{f.v}</b></div>
            <div className="ck-hf-track"><div style={{ width: f.v + '%', background: barTone(f.v) }} /></div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Müşteri 360 gerçek metrikleri (GET /api/customers/overview-metrics). Her panel ayrı
// bileşen → OverviewTab iki bağımsız sütuna dağıtabilir (kısa kart altındakiler kayar).
// Hepsi gerçek DB verisi; başka panellerde OLMAYAN bilgiler (tekrarsız).
function TrendPanel({ m }) {
  if (!m) return <div className="ck-panel"><div className="ck-panel-empty">Metrikler yükleniyor…</div></div>;
  const num = (v) => Number(v || 0);
  const ms = m.monthlySales || m.MonthlySales || [];
  const maxM = Math.max(1, ...ms.map((x) => num(x.amount ?? x.Amount)));
  const monthLbl = (mo) => {
    const y = mo.year ?? mo.Year; const mm = (mo.month ?? mo.Month ?? 1) - 1;
    return new Date(y, mm, 1).toLocaleDateString('tr-TR', { month: 'short' });
  };
  return (
    <div className="ck-panel">
      <div className="ck-panel-h"><h3><CdIcon name="trending" size={18} /> 12 Aylık Satış Trendi</h3><span>{formatTRY(num(m.sales12Mo ?? m.Sales12Mo))}</span></div>
      <div className="ck-trend" style={{ gridTemplateColumns: `repeat(${Math.max(1, ms.length)}, 1fr)` }}>
        {ms.map((x, i) => (
          <div className="ck-trend-col" key={i} title={`${monthLbl(x)}: ${formatTRY(num(x.amount ?? x.Amount))}`}>
            <div className="ck-trend-barwrap"><div className="ck-trend-bar" style={{ height: `${Math.max(3, Math.round((num(x.amount ?? x.Amount) / maxM) * 100))}%` }} /></div>
            <span className="ck-trend-lbl">{monthLbl(x)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function CategoryPanel({ m }) {
  const num = (v) => Number(v || 0);
  const cats = (m?.categoryMix || m?.CategoryMix || []).slice(0, 6);
  if (!cats.length) return null;
  const maxC = Math.max(1, ...cats.map((c) => num(c.revenue ?? c.Revenue)));
  const colors = ['#2F7FD1', '#7A4FD1', '#1E9E7A', '#D98A1E', '#D14F7A', '#00A5B8'];
  return (
    <div className="ck-panel">
      <div className="ck-panel-h"><h3><CdIcon name="box" size={18} /> Kategori Dağılımı</h3><span>Son 12 ay</span></div>
      <div className="ck-camps">
        {cats.map((c, i) => {
          const amt = num(c.revenue ?? c.Revenue);
          const pct = Math.max(4, Math.round((amt / maxC) * 100));
          return (
            <div className="ck-camp" key={i}>
              <div className="ck-camp-top"><span>{c.name ?? c.Name ?? '-'}</span><b>{formatTRY(amt)}</b></div>
              <div className="ck-camp-track"><div style={{ width: pct + '%', background: colors[i % colors.length] }} /></div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function MonthVisitPanel({ m }) {
  if (!m) return null;
  const num = (v) => Number(v || 0);
  const eq = m.equipment ?? m.Equipment;
  const ag = m.agreement ?? m.Agreement;
  return (
    <div className="ck-panel">
      <div className="ck-panel-h"><h3><CdIcon name="spark" size={16} /> Bu Ay & Ziyaret Performansı</h3><span>Son 90 gün</span></div>
      <div className="ck-metrics">
        <div><b>{num(m.skuVarietyMtd ?? m.SkuVarietyMtd)}</b><span>Ürün çeşidi</span></div>
        <div><b>{formatTRY(num(m.returnsMtd ?? m.ReturnsMtd))}</b><span>İade (bu ay)</span></div>
        <div><b>{formatTRY(num(m.salesCreditCardMtd ?? m.SalesCreditCardMtd))}</b><span>KK satış</span></div>
        <div><b>%{Math.round(num(m.hitRate ?? m.HitRate))}</b><span>Ziyaret başarı</span></div>
        <div><b>{num(m.visitCount ?? m.VisitCount)}</b><span>Ziyaret</span></div>
        <div><b>{num(m.orderedVisitCount ?? m.OrderedVisitCount)}</b><span>Siparişli ziyaret</span></div>
      </div>
      {(eq || ag) && (
        <div className="ck-sumrow">
          {eq && <span className="ck-sumchip"><CdIcon name="snow" size={14} /> {num(eq.count ?? eq.Count)} ekipman</span>}
          {ag && (ag.hasActive ?? ag.HasActive)
            ? <span className="ck-sumchip ok"><CdIcon name="filesign" size={14} /> Anlaşma: {ag.title || ag.Title || ag.type || ag.Type}</span>
            : <span className="ck-sumchip"><CdIcon name="filesign" size={14} /> Aktif anlaşma yok</span>}
        </div>
      )}
    </div>
  );
}

function ActivityPanel({ m }) {
  const acts = m?.activity ?? m?.Activity ?? [];
  if (!acts.length) return null;
  return (
    <div className="ck-panel">
      <div className="ck-panel-h"><h3><CdIcon name="clock" size={17} /> Son Aktivite</h3><span>Ziyaret · sipariş · tahsilat</span></div>
      <div className="ck-activity">
        {acts.slice(0, 8).map((a, i) => {
          const meta = ACTIVITY_META[a.type ?? a.Type] || { icon: 'clock', tone: 'gray', label: '' };
          const amt = a.amount ?? a.Amount;
          const sub = a.subtitle ?? a.Subtitle;
          return (
            <div className="ck-act" key={i}>
              <span className={`ck-act-ic ck-act-ic--${meta.tone}`}><CdIcon name={meta.icon} size={14} /></span>
              <div className="ck-act-main"><b>{a.title ?? a.Title ?? meta.label}</b>{sub && <span>{sub}</span>}</div>
              {amt != null && <span className="ck-act-amt">{formatTRY(Number(amt))}</span>}
              <span className="ck-act-date">{orderDateLabel(a.date ?? a.Date)}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function OverviewTab({ customer, orders, topSkus = [], topCampaigns = [], onOrderClick }) {
  const balanceTotal = Number(customer.currentBalance ?? 0);
  // orders zaten DocumentDate desc sıralı geliyor; yalnızca "Tamamlandı" (status 4) sipariş gösterilir.
  const lastCompleted = (orders || []).find((o) => Number(pick(o, 'status', 'Status')) === ORDER_STATUS_COMPLETED) || null;

  // Müşteri 360 gerçek metrikleri (tekrarsız zengin veri kaynağı).
  const [m, setM] = useState(null);
  useEffect(() => {
    let alive = true;
    fetch(`/api/customers/overview-metrics?id=${encodeURIComponent(customer.id)}`, { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : null))
      .then((json) => { if (alive) setM(json?.data ?? json?.Data ?? null); })
      .catch(() => {});
    return () => { alive = false; };
  }, [customer.id]);
  const mn = (v) => Number(v || 0);

  // hero3 3. kart — Son Ziyaret donut'u (gerçek: hitRate = ziyaret başarı oranı,
  // son ziyaret tarihi aktivite feed'inden).
  const vHit = Math.round(mn(m?.hitRate ?? m?.HitRate));
  const vCount = mn(m?.visitCount ?? m?.VisitCount);
  const vOrdered = mn(m?.orderedVisitCount ?? m?.OrderedVisitCount);
  const vActs = m?.activity ?? m?.Activity ?? [];
  const vLast = vActs.find((a) => (a.type ?? a.Type) === 'NOSALE') || vActs[0] || null;
  const vTone = vHit >= 70 ? 'var(--ck-green)' : vHit >= 40 ? 'var(--ck-amber)' : 'var(--ck-red)';

  return (
    <div className="ck-overview">
      {/* hero3 — prototip: navy bakiye · son sipariş · donutlu son ziyaret */}
      <div className="ck-hero3">
        <div className="ck-hcard navy">
          <div className="ck-hc-l"><CdIcon name="wallet" size={15} /> Güncel Bakiye</div>
          <div className="ck-hc-v">{formatTRY(balanceTotal)}</div>
          <div className="ck-hc-mt">{balanceTotal > 0 ? 'Açık cari bakiye' : balanceTotal < 0 ? 'Müşteri lehine bakiye' : 'Açık bakiye yok'}</div>
        </div>
        <div className="ck-hcard">
          <div className="ck-hc-l"><CdIcon name="trending" size={15} style={{ color: 'var(--ck-green)' }} /> Son Sipariş</div>
          {lastCompleted ? (
            <>
              <div className="ck-hc-v">{formatTRY(Number(pick(lastCompleted, 'netAmount', 'NetAmount') ?? 0))}</div>
              <div className="ck-hc-mt">{shortOrderNo(pick(lastCompleted, 'id', 'Id'))} · {orderDateLabel(pick(lastCompleted, 'documentDate', 'DocumentDate'))}</div>
            </>
          ) : (
            <>
              <div className="ck-hc-v">—</div>
              <div className="ck-hc-mt">Tamamlanmış sipariş yok</div>
            </>
          )}
        </div>
        <div className="ck-hcard">
          <div className="ck-hc-l"><CdIcon name="clock" size={15} /> Son Ziyaret</div>
          <div className="ck-hc-visit">
            <div className="ck-donut" style={{ background: `conic-gradient(${vTone} ${vHit}%, var(--ck-line) 0)` }}><span>%{vHit}</span></div>
            <div className="ck-hc-vi">
              <b>{vLast ? orderDateLabel(vLast.date ?? vLast.Date) : (m ? 'Ziyaret kaydı yok' : '—')}</b>
              <span>{vCount} ziyaret · {vOrdered} siparişli</span>
            </div>
          </div>
        </div>
      </div>

      {/* İki bağımsız dikey sütun (prototip .rc geniş sol) — her sütun kartlarını
          yukarıdan sıkıştırır: Top5 kısalınca ALTINDAKİ panel yukarı KAYAR; iki sütun
          aynı Y'den başlar, sağ sütun kartları aynı genişlikte → dikeyde hizalı. */}
      <div className="ck-cols">
        <div className="ck-col">
          <div className="ck-panel outlet">
            <div className="ck-panel-h"><h3><CdIcon name="box" size={18} /> Top 5 SKU</h3><span>{topSkus.length} ürün</span></div>
            {topSkus.length === 0 ? (
              <div className="ck-panel-empty">Bu müşteriye ait ürün satışı bulunamadı.</div>
            ) : (
              <table className="ck-table">
                <thead><tr><th>Ürün</th><th className="r">Miktar</th><th className="r">Ciro</th></tr></thead>
                <tbody>
                  {topSkus.map((p, i) => {
                    const code = pick(p, 'code', 'Code');
                    const name = pick(p, 'name', 'Name');
                    return (
                      <tr key={pick(p, 'productId', 'ProductId') ?? i}>
                        <td className="strong">{code ? <span className="ck-tagmini">{code}</span> : null} {name}</td>
                        <td className="r">{formatQty(Number(pick(p, 'quantity', 'Quantity') ?? 0))}</td>
                        <td className="r strong">{formatTRY(Number(pick(p, 'revenue', 'Revenue') ?? 0))}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          <TrendPanel m={m} />
          <MonthVisitPanel m={m} />
          <ActivityPanel m={m} />
        </div>

        <div className="ck-col">
          <div className="ck-panel">
            <div className="ck-panel-h"><h3><CdIcon name="spark" size={16} /> Kampanya Satışları</h3><span>{topCampaigns.length} kampanya</span></div>
            {topCampaigns.length === 0 ? (
              <div className="ck-panel-empty">Bu müşteriye ait kampanya kullanımı bulunamadı.</div>
            ) : (
              <div className="ck-camps">
                {(() => {
                  const maxD = Math.max(1, ...topCampaigns.map((c) => Number(pick(c, 'totalDiscount', 'TotalDiscount')) || 0));
                  const colors = ['#2F7FD1', '#7A4FD1', '#1E9E7A', '#D98A1E', '#D14F7A'];
                  return topCampaigns.map((c, i) => {
                    const amt = Number(pick(c, 'totalDiscount', 'TotalDiscount')) || 0;
                    const pct = Math.max(4, Math.round((amt / maxD) * 100));
                    return (
                      <div className="ck-camp" key={pick(c, 'campaignId', 'CampaignId') ?? i}>
                        <div className="ck-camp-top"><span>{pick(c, 'name', 'Name')}</span><b>{formatTRY(amt)}</b></div>
                        <div className="ck-camp-track"><div style={{ width: pct + '%', background: colors[i % colors.length] }} /></div>
                      </div>
                    );
                  });
                })()}
              </div>
            )}
            <SmartInsights customer={customer} orders={orders} topSkus={topSkus} topCampaigns={topCampaigns} />
          </div>

          <CategoryPanel m={m} />

          <div className="ck-panel orders">
            <div className="ck-panel-h"><h3><CdIcon name="clip" size={17} /> Sipariş Geçmişi</h3><span>{(orders || []).length} kayıt</span></div>
            {(orders || []).length === 0 ? (
              <div className="ck-panel-empty">Bu müşteriye ait sipariş bulunamadı.</div>
            ) : (
              <div className="ck-orders">
                {(orders || []).slice(0, 6).map((o) => {
                  const oid = pick(o, 'id', 'Id');
                  return (
                    <div className="ck-order is-clickable" key={oid} role="button" tabIndex={0}
                      onClick={() => onOrderClick?.(o)}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOrderClick?.(o); } }}
                      title="Siparişi aç">
                      <div className="ck-order-ic"><CdIcon name="box" size={16} /></div>
                      <div className="ck-order-main"><b>{shortOrderNo(oid)}</b><span>{orderDateLabel(pick(o, 'documentDate', 'DocumentDate'))}</span></div>
                      <div className="ck-order-amt">{formatTRY(Number(pick(o, 'netAmount', 'NetAmount') ?? 0))}</div>
                      <CdIcon name="chevR" size={16} style={{ color: '#9aa8b6' }} />
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <HealthScoreCard customer={customer} orders={orders} m={m} topCampaigns={topCampaigns} />
        </div>
      </div>
    </div>
  );
}

// Sistem geneliyle uyumlu confirm dialog (CustomersPage silme onayı pattern'ı).
function ConfirmDialog({ title, message, confirmLabel = 'Evet', tone = 'danger', onConfirm, onClose }) {
  const titleColor = tone === 'danger' ? '#E53E3E' : undefined;
  const confirmStyle = tone === 'danger' ? s.btnDanger : s.btnPrimary;
  return (
    <div style={s.overlay} onClick={onClose}>
      <div style={{ ...s.modal, maxWidth: 440 }} onClick={(e) => e.stopPropagation()}>
        <div style={s.modalHeader}>
          <h3 style={{ ...s.modalTitle, color: titleColor }}>{title}</h3>
        </div>
        <div style={s.modalBody}>
          <div style={{ margin: 0, color: 'var(--text-primary)', lineHeight: 1.6 }}>{message}</div>
        </div>
        <div style={s.modalFooter}>
          <button type="button" style={confirmStyle} onClick={onConfirm}>{confirmLabel}</button>
          <button type="button" style={s.btnSecondary} onClick={onClose}>İptal</button>
        </div>
      </div>
    </div>
  );
}

// Ortak modal — "+ Yeni" tıklayınca tab'lardaki formları açar.
function OpModal({ title, onClose, children, maxWidth = 600 }) {
  return (
    <div style={s.overlay} onClick={onClose}>
      <div style={{ ...s.modal, maxWidth }} onClick={(e) => e.stopPropagation()}>
        <div style={s.modalHeader}>
          <h3 style={s.modalTitle}>{title}</h3>
          <button type="button" style={s.closeBtn} onClick={onClose}>x</button>
        </div>
        <div style={s.modalBody}>{children}</div>
      </div>
    </div>
  );
}

function ListCardHeader({ title, count, onNewClick, newLabel = '+ Yeni' }) {
  return (
    <div className="cd-op-listcard-head">
      <div className="cd-op-listcard-title-row">
        <span className="cd-op-listcard-title">{title}</span>
        <span className="cd-op-listcard-meta">{count} kayıt</span>
      </div>
      {onNewClick && (
        <button type="button" className="cd-op-new-btn" onClick={onNewClick}>{newLabel}</button>
      )}
    </div>
  );
}

// ========== Tab componentleri ==========

/**
 * Mobile parity (CampaignsCard.kt): Geçerli Kampanyalar listesi.
 * Backend kampanya endpoint'i yok — mobile bunu master sync ile Room'a indiriyor,
 * web'de henüz sync altyapısı yok, bu yüzden empty state gösteriliyor.
 * İleride `/api/customers/{id}/campaigns` veya benzeri açılınca buraya bağlanır.
 */
// campaigns: müşteriye uygulanabilir olarak önceden çözülmüş kampanya listesi.
function PromoTab({ campaigns }) {
  const list = campaigns || [];
  return (
    <div className="cd-promo">
      <div className="cd-promo-head">
        <span className="cd-promo-title">Geçerli Kampanyalar</span>
        <span className="cd-promo-count">{list.length} kayıt</span>
      </div>
      {list.length === 0 ? (
        <div className="cd-promo-empty">Bu müşteriye tanımlı kampanya yok.</div>
      ) : (
        list.map((c) => {
          const id = pick(c, 'id', 'Id');
          const name = pick(c, 'name', 'Name');
          const desc = pick(c, 'shortDescription', 'ShortDescription');
          const preCond = pick(c, 'isPreConditional', 'IsPreConditional');
          return (
            <div key={id} className="cd-promo-row">
              <span className="cd-promo-icon" aria-hidden>🏷</span>
              <div className="cd-promo-body">
                <div className="cd-promo-name">{name}</div>
                {desc && <div className="cd-promo-desc">{desc}</div>}
              </div>
              {preCond && <span className="cd-promo-badge">Önşartlı</span>}
            </div>
          );
        })
      )}
    </div>
  );
}

// =========================================================================
// SurveyTab — mobile Survey feature parity (Survey/SurveyQuestion/SurveyResponse)
// =========================================================================
// Anket sekmesi — GERÇEK aktif anket kataloğu (GET /api/Surveys). Anketler global
// tanımdır, saha ekibi mobilde doldurur; müşteri detayında hangi anketlerin aktif
// olduğu + soru sayısı gösterilir (localStorage mock kaldırıldı).
function SurveyTab({ customer }) {
  const [surveys, setSurveys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('ALL');

  useEffect(() => {
    let alive = true;
    setLoading(true);
    fetch('/api/Surveys?isActive=true&take=100', { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : null))
      .then((json) => {
        if (!alive) return;
        const payload = json?.data ?? json?.Data ?? json;
        const rows = payload?.items ?? payload?.value ?? (Array.isArray(payload) ? payload : []);
        setSurveys(Array.isArray(rows) ? rows : []);
      })
      .catch(() => { if (alive) setSurveys([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [customer.id]);

  const types = Array.from(new Set(surveys.map((s) => pick(s, 'surveyType', 'SurveyType')).filter(Boolean)));
  const filtered = filter === 'ALL' ? surveys : surveys.filter((s) => pick(s, 'surveyType', 'SurveyType') === filter);

  return (
    <div className="cd-op-card">
      <h3 className="cd-op-card-title">Anketler</h3>
      <p className="cd-op-card-subtitle">Aktif anket kataloğu · saha ekibi mobilde doldurur</p>
      {types.length > 1 && (
        <div className="cd-op-pillrow">
          <button type="button" className={`cd-op-pill${filter === 'ALL' ? ' is-active' : ''}`} onClick={() => setFilter('ALL')}>Tümü</button>
          {types.map((t) => (
            <button key={t} type="button" className={`cd-op-pill${filter === t ? ' is-active' : ''}`} onClick={() => setFilter(t)}>{t}</button>
          ))}
        </div>
      )}
      <div className="cd-op-list">
        {loading ? (
          <div className="cd-op-list-empty">Yükleniyor…</div>
        ) : filtered.length === 0 ? (
          <div className="cd-op-list-empty">Aktif anket bulunamadı.</div>
        ) : filtered.map((sv) => {
          const active = pick(sv, 'isActive', 'IsActive');
          const qc = pick(sv, 'questionCount', 'QuestionCount') ?? 0;
          return (
            <div key={pick(sv, 'id', 'Id')} className="cd-op-list-row">
              <span className="cd-op-list-icon" style={{ background: 'rgba(0, 115, 234, 0.10)', color: 'var(--accent)' }} aria-hidden>📋</span>
              <div className="cd-op-list-body">
                <div className="cd-op-list-title">{pick(sv, 'title', 'Title') || '-'}</div>
                <div className="cd-op-list-meta">{pick(sv, 'surveyType', 'SurveyType') || 'Anket'} · {qc} soru</div>
              </div>
              <span className={`cd-op-badge ${active ? 'cd-op-badge--ok' : 'cd-op-badge--wait'}`}>{active ? 'Aktif' : 'Pasif'}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// =========================================================================
// CoolerTab — mobile Cooler feature parity (CoolerFaultReport)
// =========================================================================
function CoolerTab({ customer, onToast }) {
  const [records, setRecords] = useState(() => loadLocal(STORAGE_KEYS.coolerFaults, customer.id));
  const [serverRecords, setServerRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [barcode, setBarcode] = useState('');
  const [affectedDoor, setAffectedDoor] = useState('');
  const [reason, setReason] = useState('NOT_COOLING');
  const [urgency, setUrgency] = useState('NORMAL');
  const [note, setNote] = useState('');

  useEffect(() => { setRecords(loadLocal(STORAGE_KEYS.coolerFaults, customer.id)); }, [customer.id]);

  // Gerçek backend verisi: GET /api/cooler-fault-reports?customerId= (CoolerFaultReportsController).
  // Yanıt ApiResponse zarfıdır → satırlar json.data.items içinde. Alanlar render ile 1:1 uyumlu
  // (id, reason, urgency, trackingNo, coolerBarcode, affectedDoor, reportedAt). syncStatus yok →
  // sunucu kaydı zaten senkron, "pending" noktası çıkmaz.
  useEffect(() => {
    let alive = true;
    setLoading(true);
    fetch(`/api/cooler-fault-reports?customerId=${encodeURIComponent(customer.id)}`, { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : null))
      .then((json) => {
        if (!alive) return;
        const payload = json?.data ?? json;
        const rows = Array.isArray(payload) ? payload : (payload?.items ?? payload?.value ?? []);
        setServerRecords(Array.isArray(rows) ? rows : []);
      })
      .catch(() => { if (alive) setServerRecords([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [customer.id]);

  // Yerel bekleyen kayıtlar (pending) önce, sonra sunucudan gelenler.
  const displayRecords = [...records, ...serverRecords];

  const openNew = () => {
    setBarcode(''); setAffectedDoor(''); setReason('NOT_COOLING'); setUrgency('NORMAL'); setNote('');
    setModalOpen(true);
  };

  const canSubmit = barcode.trim() && reason && urgency;

  const handleSubmit = () => {
    if (!canSubmit) return;
    const rec = {
      id: `local-${Date.now()}`,
      pointId: customer.id,
      coolerBarcode: barcode.trim(),
      affectedDoor: affectedDoor.trim() || null,
      reason,
      urgency,
      note: note.trim(),
      reportedAt: new Date().toISOString(),
      trackingNo: `TRK-${Date.now().toString().slice(-6)}`,
      syncStatus: 'pending',
    };
    const next = [rec, ...records];
    setRecords(next);
    saveLocal(STORAGE_KEYS.coolerFaults, customer.id, next);
    setModalOpen(false);
    onToast?.('success', `Arıza kaydedildi · ${rec.trackingNo}`);
  };

  return (
    <>
      <div className="ck-listwrap">
        <div className="ck-tabhead">
          <h3>Soğutucu Arıza Bildirimleri <span className="ck-count">{displayRecords.length} kayıt</span></h3>
          <button type="button" className="ck-addbtn" onClick={openNew}><CdIcon name="plus" size={17} /> Yeni Arıza</button>
        </div>
        {loading && displayRecords.length === 0 ? (
          <div className="ck-empty"><p>Yükleniyor…</p></div>
        ) : displayRecords.length === 0 ? (
          <div className="ck-empty"><span className="ck-empty-ic"><CdIcon name="snow" size={30} /></span><p>Henüz arıza bildirimi yok. “Yeni Arıza” ile ekleyebilirsin.</p></div>
        ) : (
          <div className="ck-list">
            {displayRecords.map((r) => {
              const reasonLabel = COOLER_REASONS.find((x) => x.value === r.reason)?.label || r.reason;
              const urgencyLabel = COOLER_URGENCY.find((x) => x.value === r.urgency)?.label || r.urgency;
              const metaParts = [r.trackingNo, r.coolerBarcode, r.affectedDoor ? `Kapı ${r.affectedDoor}` : null].filter(Boolean);
              return (
                <div key={r.id} className="ck-li">
                  <div className="ck-li-ic cyan"><CdIcon name="snow" size={18} /></div>
                  <div className="ck-li-main">
                    <b>{reasonLabel}{r.syncStatus === 'pending' && <span className="cd-recent-pending" title="Sunucuya senkronize edilmedi">●</span>}</b>
                    <span>{metaParts.join(' · ')}</span>
                  </div>
                  <div className="ck-li-right">
                    <span className={'ck-pill ' + pillTone(urgencyLabel)}>{urgencyLabel}</span>
                    <span className="ck-li-by">{relativeLabel(r.reportedAt)}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {modalOpen && (
        <OpModal title="Yeni Soğutucu Arıza" onClose={() => setModalOpen(false)} maxWidth={560}>
          <div className="cd-modal-form">
            <div className="cd-op-grid-2">
              <div className="cd-op-field">
                <label>Soğutucu Barkod</label>
                <input className="cd-op-input" value={barcode} onChange={(e) => setBarcode(e.target.value)} placeholder="Barkod / seri no" autoFocus />
              </div>
              <div className="cd-op-field">
                <label>Etkilenen Kapı</label>
                <input className="cd-op-input" value={affectedDoor} onChange={(e) => setAffectedDoor(e.target.value)} placeholder="(boş = tüm cihaz)" />
              </div>
              <div className="cd-op-field">
                <label>Arıza Sebebi</label>
                <select className="cd-op-select" value={reason} onChange={(e) => setReason(e.target.value)}>
                  {COOLER_REASONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                </select>
              </div>
              <div className="cd-op-field">
                <label>Aciliyet</label>
                <select className="cd-op-select" value={urgency} onChange={(e) => setUrgency(e.target.value)}>
                  {COOLER_URGENCY.map((u) => <option key={u.value} value={u.value}>{u.label}</option>)}
                </select>
              </div>
            </div>
            <div className="cd-op-field">
              <label>Not</label>
              <textarea className="cd-op-textarea" value={note} onChange={(e) => setNote(e.target.value)} />
            </div>
          </div>
          <div className="cd-modal-footer">
            <button type="button" className="cd-modal-cancel-btn" onClick={() => setModalOpen(false)}>İptal</button>
            <button type="button" className="cd-op-submit" disabled={!canSubmit} onClick={handleSubmit}>Arıza Bildir</button>
          </div>
        </OpModal>
      )}
    </>
  );
}

// =========================================================================
// StandCountTab — mobile StandCount feature parity (StandCountItem/Record)
// =========================================================================
function StandCountTab({ customer, onToast }) {
  const [records, setRecords] = useState(() => loadLocal(STORAGE_KEYS.standCounts, customer.id));
  const [serverRecords, setServerRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [counts, setCounts] = useState(() => Object.fromEntries(PRODUCT_DEMO_LIST.map((p) => [p.code, 0])));

  useEffect(() => { setRecords(loadLocal(STORAGE_KEYS.standCounts, customer.id)); }, [customer.id]);

  // Gerçek backend verisi: GET /api/stand-counts?customerId= (StandCountsController).
  // Yanıt ApiResponse zarfı: { data: { items: [...], okCount, ... } }.
  useEffect(() => {
    let alive = true;
    setLoading(true);
    fetch(`/api/stand-counts?customerId=${encodeURIComponent(customer.id)}`, { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : null))
      .then((json) => {
        if (!alive) return;
        const payload = json?.data ?? json;
        const rows = Array.isArray(payload) ? payload : (payload?.items ?? payload?.value ?? []);
        const mapped = (Array.isArray(rows) ? rows : []).map((row) => ({
          id: row.id,
          totalUnits: row.totalQuantity ?? 0,
          items: Array.from({ length: row.itemCount || 0 }),
          createdAt: row.countedAt || row.createdDate,
        }));
        setServerRecords(mapped);
      })
      .catch(() => { if (alive) setServerRecords([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [customer.id]);

  // Yerel/pending kayıtlar önce, ardından sunucudan gelenler.
  const allRecords = [...records, ...serverRecords];

  const openNew = () => {
    setCounts(Object.fromEntries(PRODUCT_DEMO_LIST.map((p) => [p.code, 0])));
    setModalOpen(true);
  };

  const bump = (code, delta) => setCounts((p) => ({ ...p, [code]: Math.max(0, (p[code] || 0) + delta) }));
  const set = (code, val) => setCounts((p) => ({ ...p, [code]: Math.max(0, Number(val) || 0) }));

  const totalUnits = Object.values(counts).reduce((a, b) => a + (Number(b) || 0), 0);
  const countedProducts = Object.values(counts).filter((v) => Number(v) > 0).length;

  const handleSubmit = () => {
    if (totalUnits === 0) return;
    const items = PRODUCT_DEMO_LIST.map((p) => ({
      productCode: p.code,
      productName: p.name,
      countedQty: Number(counts[p.code] || 0),
    })).filter((i) => i.countedQty > 0);
    const rec = {
      id: `local-${Date.now()}`,
      pointId: customer.id,
      items,
      totalUnits,
      createdAt: new Date().toISOString(),
      isDraft: false,
      syncStatus: 'pending',
    };
    const next = [rec, ...records];
    setRecords(next);
    saveLocal(STORAGE_KEYS.standCounts, customer.id, next);
    setModalOpen(false);
    onToast?.('success', `Sayım kaydedildi · ${totalUnits} adet`);
  };

  return (
    <>
      <div className="cd-op-listcard">
        <ListCardHeader title="Stand Sayımları" count={allRecords.length} onNewClick={openNew} newLabel="+ Yeni Sayım" />
        <div className="cd-op-listcard-body">
          <div className="cd-op-list">
            {loading ? (
              <div className="cd-op-list-empty">Yükleniyor…</div>
            ) : allRecords.length === 0 ? (
              <div className="cd-op-list-empty">Henüz sayım yok.</div>
            ) : allRecords.map((r) => (
              <div key={r.id} className="cd-op-list-row">
                <span className="cd-op-list-icon" style={{ background: 'rgba(0, 200, 117, 0.10)', color: '#16a34a' }} aria-hidden>📊</span>
                <div className="cd-op-list-body">
                  <div className="cd-op-list-title">
                    {r.totalUnits} adet · {r.items.length} ürün
                    {r.syncStatus === 'pending' && <span className="cd-op-badge cd-op-badge--pending">pending</span>}
                  </div>
                  <div className="cd-op-list-meta">{relativeLabel(r.createdAt)}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {modalOpen && (
        <OpModal title="Yeni Stand Sayım" onClose={() => setModalOpen(false)} maxWidth={560}>
          <p className="cd-op-card-subtitle">{countedProducts}/{PRODUCT_DEMO_LIST.length} ürün · Toplam {totalUnits} adet</p>
          <div className="cd-op-list">
            {PRODUCT_DEMO_LIST.map((p) => (
              <div key={p.code} className="cd-op-list-row">
                <span className="cd-op-list-icon" style={{ background: 'rgba(0, 200, 117, 0.10)', color: '#16a34a' }} aria-hidden>📦</span>
                <div className="cd-op-list-body">
                  <div className="cd-op-list-title">{p.name}</div>
                  <div className="cd-op-list-meta">{p.code}</div>
                </div>
                <div className="cd-op-counter">
                  <button type="button" className="cd-op-counter-btn" onClick={() => bump(p.code, -1)}>−</button>
                  <input type="number" className="cd-op-counter-value" style={{ width: 48, border: 0, background: 'transparent', textAlign: 'center' }} value={counts[p.code] || 0} onChange={(e) => set(p.code, e.target.value)} />
                  <button type="button" className="cd-op-counter-btn" onClick={() => bump(p.code, 1)}>+</button>
                </div>
              </div>
            ))}
          </div>
          <div className="cd-modal-footer">
            <button type="button" className="cd-modal-cancel-btn" onClick={() => setModalOpen(false)}>İptal</button>
            <button type="button" className="cd-op-submit" disabled={totalUnits === 0} onClick={handleSubmit}>Sayımı Kaydet</button>
          </div>
        </OpModal>
      )}
    </>
  );
}

// =========================================================================
// AgreementTab — mobile Agreement feature parity (read-only)
// =========================================================================
function AgreementTab({ customer }) {
  const [selectedType, setSelectedType] = useState('ALL');
  const [agreements, setAgreements] = useState([]);
  const [loading, setLoading] = useState(true);

  // Gerçek backend verisi: GET /api/Agreements?customerId= (AgreementsController).
  useEffect(() => {
    let alive = true;
    setLoading(true);
    fetch(`/api/Agreements?customerId=${encodeURIComponent(customer.id)}`, { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : null))
      .then((json) => {
        if (!alive) return;
        const rows = Array.isArray(json) ? json : (json?.data ?? json?.value ?? []);
        setAgreements(Array.isArray(rows) ? rows : []);
      })
      .catch(() => { if (alive) setAgreements([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [customer.id]);

  const filtered = selectedType === 'ALL' ? agreements : agreements.filter((a) => a.type === selectedType);

  return (
    <div className="cd-op-card">
      <h3 className="cd-op-card-title">Anlaşmalar</h3>
      <p className="cd-op-card-subtitle">Müşteriye tanımlı anlaşmalar (read-only)</p>
      <div className="cd-op-pillrow">
        <button type="button" className={`cd-op-pill${selectedType === 'ALL' ? ' is-active' : ''}`} onClick={() => setSelectedType('ALL')}>Tümü</button>
        {AGREEMENT_TYPES.map((t) => (
          <button key={t.value} type="button" className={`cd-op-pill${selectedType === t.value ? ' is-active' : ''}`} onClick={() => setSelectedType(t.value)}>{t.label}</button>
        ))}
      </div>
      <div className="cd-op-list">
        {loading ? (
          <div className="cd-op-list-empty">Yükleniyor…</div>
        ) : filtered.length === 0 ? (
          <div className="cd-op-list-empty">Bu müşteriye tanımlı anlaşma yok.</div>
        ) : filtered.map((a) => (
          <div key={a.id} className="cd-op-list-row">
            <span className="cd-op-list-icon" style={{ background: 'rgba(99, 102, 241, 0.10)', color: '#6366f1' }} aria-hidden>📄</span>
            <div className="cd-op-list-body">
              <div className="cd-op-list-title">{a.title}</div>
              <div className="cd-op-list-meta">{a.type} · {a.startDate} → {a.endDate}</div>
            </div>
            <span className={`cd-op-badge ${a.status === 'ACTIVE' ? 'cd-op-badge--ok' : 'cd-op-badge--wait'}`}>{a.status}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// =========================================================================
// CertificatedReturnTab — mobile DocumentReturn parity (simplified single-form)
// =========================================================================
function CertificatedReturnTab({ customer, onToast }) {
  const [records, setRecords] = useState(() => loadLocal(STORAGE_KEYS.returns, customer.id));
  const [modalOpen, setModalOpen] = useState(false);
  const [type, setType] = useState('CUSTOMER_RECEIPT');
  const [returnNo, setReturnNo] = useState('');
  const [sourceDocumentNo, setSourceDocumentNo] = useState('');
  const [sourceDocumentDate, setSourceDocumentDate] = useState('');
  const [productCode, setProductCode] = useState('');
  const [productName, setProductName] = useState('');
  const [quantity, setQuantity] = useState('');
  const [notes, setNotes] = useState('');

  useEffect(() => { setRecords(loadLocal(STORAGE_KEYS.returns, customer.id)); }, [customer.id]);

  const openNew = () => {
    setType('CUSTOMER_RECEIPT'); setReturnNo(''); setSourceDocumentNo(''); setSourceDocumentDate('');
    setProductCode(''); setProductName(''); setQuantity(''); setNotes('');
    setModalOpen(true);
  };

  const qty = Number(quantity) || 0;
  const canSubmit = type && sourceDocumentNo.trim() && productCode.trim() && qty > 0;

  const handleSubmit = () => {
    if (!canSubmit) return;
    const rec = {
      id: `local-${Date.now()}`,
      pointId: customer.id,
      type,
      returnNo: returnNo.trim() || `RT-${Date.now().toString().slice(-6)}`,
      sourceDocumentNo: sourceDocumentNo.trim(),
      sourceDocumentDate: sourceDocumentDate || null,
      lines: [{ productCode: productCode.trim(), productName: productName.trim() || productCode.trim(), quantity: qty }],
      notes: notes.trim(),
      totalQty: qty,
      status: 'DRAFT',
      createdAt: new Date().toISOString(),
      syncStatus: 'pending',
    };
    const next = [rec, ...records];
    setRecords(next);
    saveLocal(STORAGE_KEYS.returns, customer.id, next);
    setModalOpen(false);
    onToast?.('success', `İade kaydedildi · ${rec.returnNo}`);
  };

  return (
    <>
      <div className="cd-op-listcard">
        <ListCardHeader title="Belgeli İadeler" count={records.length} onNewClick={openNew} newLabel="+ Yeni İade" />
        <div className="cd-op-listcard-body">
          <div className="cd-op-list">
            {records.length === 0 ? (
              <div className="cd-op-list-empty">Henüz iade yok.</div>
            ) : records.map((r) => {
              const typeLabel = RETURN_TYPES.find((t) => t.value === r.type)?.label || r.type;
              return (
                <div key={r.id} className="cd-op-list-row">
                  <span className="cd-op-list-icon" style={{ background: 'rgba(226, 68, 92, 0.10)', color: '#dc2626' }} aria-hidden>↩</span>
                  <div className="cd-op-list-body">
                    <div className="cd-op-list-title">
                      {r.returnNo}
                      {r.syncStatus === 'pending' && <span className="cd-op-badge cd-op-badge--pending">pending</span>}
                    </div>
                    <div className="cd-op-list-meta">{typeLabel} · {r.totalQty} adet · {relativeLabel(r.createdAt)}</div>
                  </div>
                  <span className="cd-op-list-trailing">#{r.sourceDocumentNo}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {modalOpen && (
        <OpModal title="Yeni Belgeli İade" onClose={() => setModalOpen(false)} maxWidth={640}>
          <div className="cd-modal-form">
            <div className="cd-op-grid-2">
              <div className="cd-op-field">
                <label>Belge Tipi</label>
                <select className="cd-op-select" value={type} onChange={(e) => setType(e.target.value)}>
                  {RETURN_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
              <div className="cd-op-field">
                <label>İade No (opsiyonel)</label>
                <input className="cd-op-input" value={returnNo} onChange={(e) => setReturnNo(e.target.value)} placeholder="Otomatik üretilir" />
              </div>
              <div className="cd-op-field">
                <label>Kaynak Belge No</label>
                <input className="cd-op-input" value={sourceDocumentNo} onChange={(e) => setSourceDocumentNo(e.target.value)} autoFocus />
              </div>
              <div className="cd-op-field">
                <label>Belge Tarihi</label>
                <input type="date" className="cd-op-input" value={sourceDocumentDate} onChange={(e) => setSourceDocumentDate(e.target.value)} />
              </div>
              <div className="cd-op-field">
                <label>Ürün Kodu</label>
                <input className="cd-op-input" value={productCode} onChange={(e) => setProductCode(e.target.value)} />
              </div>
              <div className="cd-op-field">
                <label>Ürün Adı</label>
                <input className="cd-op-input" value={productName} onChange={(e) => setProductName(e.target.value)} placeholder="(boş = kod kullanılır)" />
              </div>
              <div className="cd-op-field">
                <label>Miktar</label>
                <input type="number" className="cd-op-input" value={quantity} onChange={(e) => setQuantity(e.target.value)} min="1" />
              </div>
            </div>
            <div className="cd-op-field">
              <label>Not</label>
              <textarea className="cd-op-textarea" value={notes} onChange={(e) => setNotes(e.target.value)} />
            </div>
          </div>
          <div className="cd-modal-footer">
            <button type="button" className="cd-modal-cancel-btn" onClick={() => setModalOpen(false)}>İptal</button>
            <button type="button" className="cd-op-submit" disabled={!canSubmit} onClick={handleSubmit}>İadeyi Kaydet</button>
          </div>
        </OpModal>
      )}
    </>
  );
}

// =========================================================================
// DepositCountTab — mobile DepositCount parity (BOTTLE_GLASS/PET/CRATE/CO2)
// =========================================================================
function DepositCountTab({ customer, onToast }) {
  const [records, setRecords] = useState(() => loadLocal(STORAGE_KEYS.deposits, customer.id));
  const [modalOpen, setModalOpen] = useState(false);
  const [shipmentDate, setShipmentDate] = useState('');
  const [quantities, setQuantities] = useState(() => Object.fromEntries(DEPOSIT_TYPES.map((t) => [t.value, 0])));

  useEffect(() => { setRecords(loadLocal(STORAGE_KEYS.deposits, customer.id)); }, [customer.id]);

  const openNew = () => {
    setQuantities(Object.fromEntries(DEPOSIT_TYPES.map((t) => [t.value, 0])));
    setShipmentDate('');
    setModalOpen(true);
  };

  const bump = (type, delta) => setQuantities((p) => ({ ...p, [type]: Math.max(0, (p[type] || 0) + delta) }));
  const set = (type, val) => setQuantities((p) => ({ ...p, [type]: Math.max(0, Number(val) || 0) }));

  const totalUnits = Object.values(quantities).reduce((a, b) => a + (Number(b) || 0), 0);

  const handleSubmit = () => {
    if (totalUnits === 0) return;
    const lines = DEPOSIT_TYPES
      .map((t) => ({ type: t.value, quantity: Number(quantities[t.value] || 0), notes: '' }))
      .filter((l) => l.quantity > 0);
    const rec = {
      id: `local-${Date.now()}`,
      sdmId: customer.id,
      sdmName: customer.title || customer.textId || '',
      shipmentDate: shipmentDate || null,
      lines,
      totalUnits,
      createdAt: new Date().toISOString(),
      isDraft: false,
      syncStatus: 'pending',
    };
    const next = [rec, ...records];
    setRecords(next);
    saveLocal(STORAGE_KEYS.deposits, customer.id, next);
    setModalOpen(false);
    onToast?.('success', `Depozito sayımı kaydedildi · ${totalUnits} adet`);
  };

  return (
    <>
      <div className="ck-listwrap">
        <div className="ck-tabhead">
          <h3>Depozito Sayımları <span className="ck-count">{records.length} kayıt</span></h3>
          <button type="button" className="ck-addbtn" onClick={openNew}><CdIcon name="plus" size={17} /> Yeni Sayım</button>
        </div>
        {records.length === 0 ? (
          <div className="ck-empty"><span className="ck-empty-ic"><CdIcon name="bottle" size={30} /></span><p>Henüz depozito sayımı yok. “Yeni Sayım” ile ekleyebilirsin.</p></div>
        ) : (
          <div className="ck-list">
            {records.map((r) => {
              const breakdown = r.lines.map((l) => {
                const t = DEPOSIT_TYPES.find((x) => x.value === l.type);
                return `${t?.label || l.type} ${l.quantity}`;
              }).join(' · ');
              return (
                <div key={r.id} className="ck-li">
                  <div className="ck-li-ic teal"><CdIcon name="bottle" size={18} /></div>
                  <div className="ck-li-main">
                    <b>{r.totalUnits} kap{r.syncStatus === 'pending' && <span className="cd-recent-pending" title="Sunucuya senkronize edilmedi">●</span>}</b>
                    <span>{breakdown}{r.shipmentDate ? ` · Sevk ${r.shipmentDate}` : ''}</span>
                  </div>
                  <div className="ck-li-right">
                    <span className="ck-li-date">{relativeLabel(r.createdAt)}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {modalOpen && (
        <OpModal title="Yeni Depozito Sayımı" onClose={() => setModalOpen(false)} maxWidth={520}>
          <div className="cd-op-field">
            <label>Sevkiyat Tarihi</label>
            <input type="date" className="cd-op-input" value={shipmentDate} onChange={(e) => setShipmentDate(e.target.value)} />
          </div>
          <div className="cd-op-list">
            {DEPOSIT_TYPES.map((t) => (
              <div key={t.value} className="cd-op-list-row">
                <span className="cd-op-list-icon" style={{ background: 'rgba(99, 102, 241, 0.10)', color: '#4f46e5' }} aria-hidden>{t.icon}</span>
                <div className="cd-op-list-body">
                  <div className="cd-op-list-title">{t.label}</div>
                </div>
                <div className="cd-op-counter">
                  <button type="button" className="cd-op-counter-btn" onClick={() => bump(t.value, -1)}>−</button>
                  <input type="number" className="cd-op-counter-value" style={{ width: 56, border: 0, background: 'transparent', textAlign: 'center' }} value={quantities[t.value] || 0} onChange={(e) => set(t.value, e.target.value)} />
                  <button type="button" className="cd-op-counter-btn" onClick={() => bump(t.value, 1)}>+</button>
                </div>
              </div>
            ))}
          </div>
          <div className="cd-op-stat-row">
            <div className="cd-op-stat">
              <div className="cd-op-stat-label">Toplam</div>
              <div className="cd-op-stat-value">{totalUnits}</div>
            </div>
          </div>
          <div className="cd-modal-footer">
            <button type="button" className="cd-modal-cancel-btn" onClick={() => setModalOpen(false)}>İptal</button>
            <button type="button" className="cd-op-submit" disabled={totalUnits === 0} onClick={handleSubmit}>Sayımı Kaydet</button>
          </div>
        </OpModal>
      )}
    </>
  );
}

// FinanceMethod (backend enum) → PAYMENT_CHANNELS.value eşlemesi.
const FINANCE_METHOD_TO_CHANNEL = { 1: 'CASH', 2: 'CARD', 3: 'CHECK', 4: 'PROMISSORY_NOTE', 5: 'BANK_TRANSFER' };

function CollectionTab({ customer, onToast }) {
  const [records, setRecords] = useState(() => loadLocalPayments(customer.id));
  const [serverRecords, setServerRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [amount, setAmount] = useState('');
  const [documentNo, setDocumentNo] = useState('');
  const [payerName, setPayerName] = useState('');
  const [channel, setChannel] = useState('CASH');

  useEffect(() => {
    setRecords(loadLocalPayments(customer.id));
  }, [customer.id]);

  // Gerçek backend verisi: FinanceTransactions taban [HttpGet] OData'dır (customerId query paramı YOK) →
  // müşteriye göre daralt: $filter=CustomerId eq {guid} and Direction eq 1 (Collection=Tahsilat).
  // OData yanıtı { value: [...] }. Alanlar entity (PascalCase) → render alanlarına map edilir; Method
  // (int) → PAYMENT_CHANNELS.value'ya çevrilir.
  useEffect(() => {
    let alive = true;
    setLoading(true);
    const filter = `CustomerId eq ${customer.id} and Direction eq 1`;
    const url = `/api/financetransactions?$filter=${encodeURIComponent(filter)}&$orderby=TransactionDate desc&$top=200`;
    fetch(url, { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : null))
      .then((json) => {
        if (!alive) return;
        const payload = json?.data ?? json;
        const rows = Array.isArray(payload) ? payload : (payload?.value ?? payload?.items ?? []);
        const mapped = (Array.isArray(rows) ? rows : []).map((x) => ({
          id: x.Id ?? x.id,
          documentNo: x.DocumentNo ?? x.documentNo ?? '',
          payerName: x.DrawerName ?? x.drawerName ?? '',
          amount: Number(x.Amount ?? x.amount ?? 0),
          channel: FINANCE_METHOD_TO_CHANNEL[x.Method ?? x.method] || 'CASH',
          createdAtIso: x.TransactionDate ?? x.transactionDate ?? x.CreatedDate ?? x.createdDate,
          // Sunucudan gelen kayıt zaten senkron → "pending" noktası çıkmaz.
        }));
        setServerRecords(mapped);
      })
      .catch(() => { if (alive) setServerRecords([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [customer.id]);

  // Yerel bekleyen kayıtlar (pending) önce, sonra sunucudan gelenler.
  const displayRecords = [...records, ...serverRecords];

  const openNew = () => {
    setAmount(''); setDocumentNo(''); setPayerName(''); setChannel('CASH');
    setModalOpen(true);
  };

  const numericAmount = Number(String(amount).replace(/[^0-9.,]/g, '').replace(',', '.')) || 0;
  const canSubmit = numericAmount > 0 && documentNo.trim() && payerName.trim();

  const handleSubmit = () => {
    if (!canSubmit) return;
    if (!window.confirm(`${formatTRY(numericAmount)} tutarında tahsilatı onaylıyor musunuz?`)) return;
    const nowIso = new Date().toISOString();
    const newRec = {
      id: `local-${Date.now()}`,
      pointId: customer.id,
      documentNo: documentNo.trim(),
      payerName: payerName.trim(),
      amount: numericAmount,
      channel,
      createdAtIso: nowIso,
      syncStatus: 'pending',  // mobile gibi: backend Payment apply yokken queue'da bekler
    };
    const next = [newRec, ...records];
    setRecords(next);
    saveLocalPayments(customer.id, next);
    setModalOpen(false);
    onToast?.('success', `Tahsilat alındı — ${formatTRY(numericAmount)} (${PAYMENT_CHANNELS.find((c) => c.value === channel)?.label || channel})`);
  };

  return (
    <>
      <div className="ck-listwrap">
        <div className="ck-tabhead">
          <h3>Tahsilatlar <span className="ck-count">{displayRecords.length} kayıt</span></h3>
          <button type="button" className="ck-addbtn" onClick={openNew}><CdIcon name="plus" size={17} /> Yeni Tahsilat</button>
        </div>
        {loading && displayRecords.length === 0 ? (
          <div className="ck-empty"><p>Yükleniyor…</p></div>
        ) : displayRecords.length === 0 ? (
          <div className="ck-empty"><span className="ck-empty-ic"><CdIcon name="banknote" size={30} /></span><p>Henüz tahsilat yok. “Yeni Tahsilat” ile ekleyebilirsin.</p></div>
        ) : (
          <div className="ck-list">
            {displayRecords.map((rec) => {
              const ch = PAYMENT_CHANNELS.find((c) => c.value === rec.channel) || PAYMENT_CHANNELS[0];
              const isPending = rec.syncStatus === 'pending';
              return (
                <div key={rec.id} className="ck-li">
                  <div className="ck-li-ic green"><CdIcon name="banknote" size={18} /></div>
                  <div className="ck-li-main">
                    <b>{formatTRY(rec.amount)}{isPending && <span className="cd-recent-pending" title="Sunucuya senkronize edilmedi">●</span>}</b>
                    <span>{rec.documentNo} · {ch.label}</span>
                  </div>
                  <div className="ck-li-right">
                    <span className="ck-li-date">{relativeLabel(rec.createdAtIso)}</span>
                    {rec.payerName && <span className="ck-li-by">{rec.payerName}</span>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {modalOpen && (
        <OpModal title="Yeni Tahsilat" onClose={() => setModalOpen(false)} maxWidth={560}>
          <div className="cd-modal-form">
            <div>
              <h4 className="cd-coll-panel-title">Tahsil Edilecek Tutar</h4>
              <div className="cd-coll-amount-display">
                <span className="cd-coll-amount-currency">₺</span>
                <input
                  type="text"
                  inputMode="decimal"
                  className="cd-coll-amount-value"
                  placeholder="0,00"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  autoFocus
                />
                {amount && (
                  <button type="button" className="cd-coll-amount-clear" onClick={() => setAmount('')}>Sıfırla</button>
                )}
              </div>
              <div className="cd-coll-chips" style={{ marginTop: 8 }}>
                {QUICK_AMOUNTS.map((v) => (
                  <button key={v} type="button" className="cd-coll-chip" onClick={() => setAmount(String(v))}>
                    {formatTRYShort(v)}
                  </button>
                ))}
              </div>
            </div>

            <div className="cd-op-grid-2">
              <div className="cd-op-field">
                <label>Belge numarası</label>
                <input className="cd-op-input" value={documentNo} onChange={(e) => setDocumentNo(e.target.value)} />
              </div>
              <div className="cd-op-field">
                <label>Tahsil eden / Temsilci</label>
                <input className="cd-op-input" value={payerName} onChange={(e) => setPayerName(e.target.value)} />
              </div>
            </div>

            <div>
              <h4 className="cd-coll-panel-title">Ödeme Şekli</h4>
              <div className="cd-coll-channels">
                {PAYMENT_CHANNELS.map((ch) => {
                  const isActive = channel === ch.value;
                  return (
                    <button
                      key={ch.value}
                      type="button"
                      className={`cd-coll-channel${isActive ? ' is-active' : ''}`}
                      onClick={() => setChannel(ch.value)}
                      aria-pressed={isActive}
                    >
                      <span className="cd-coll-channel-icon" style={{ background: `${ch.color}1F`, color: ch.color }} aria-hidden>{ch.icon}</span>
                      <span>{ch.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
          <div className="cd-modal-footer">
            <button type="button" className="cd-modal-cancel-btn" onClick={() => setModalOpen(false)}>İptal</button>
            <button type="button" className="cd-coll-submit" style={{ padding: '12px 18px' }} disabled={!canSubmit} onClick={handleSubmit}>💰 Tahsil Et</button>
          </div>
        </OpModal>
      )}
    </>
  );
}

function InfoRow({ icon, label, value }) {
  if (value === null || value === undefined || value === '') return null;
  return (
    <div className="cd-info-row">
      {icon && <span className="cd-info-ico" aria-hidden><CdIcon name={icon} /></span>}
      <div className="cd-info-text">
        <dt>{label}</dt>
        <dd>{value}</dd>
      </div>
    </div>
  );
}

// Sol karttaki gruplu bilgi bölümü (başlık + ikonlu satırlar). Tüm satırlar boşsa bölüm gizlenir.
function InfoSection({ title, children }) {
  const hasContent = Array.isArray(children) ? children.some(Boolean) : Boolean(children);
  if (!hasContent) return null;
  return (
    <div className="cd-info-section">
      <div className="cd-info-section-title">{title}</div>
      <dl className="cd-info">{children}</dl>
    </div>
  );
}

// Sol panel — müşteriye dair GERÇEK veri özetleri (bakiye + sipariş istatistikleri).
// orders parent'ta zaten çekiliyor; ek fetch yok, mock yok.
function SidebarStats({ customer, orders = [] }) {
  const balance = Number(customer?.currentBalance ?? 0);
  const list = orders || [];
  const cnt = list.length;
  const total = list.reduce((sum, o) => sum + (Number(pick(o, 'netAmount', 'NetAmount')) || 0), 0);
  const avg = cnt ? total / cnt : 0;
  const lastRaw = cnt ? pick(list[0], 'documentDate', 'DocumentDate') : null;
  const dd = lastRaw ? new Date(lastRaw) : null;
  const days = dd && !isNaN(dd.getTime()) ? Math.floor((Date.now() - dd.getTime()) / 86400000) : null;
  // Önerilen aksiyon — kural tabanlı (bakiye + sipariş durgunluğu).
  let act = { tone: 'g', title: 'Rutin takip', text: 'Özel bir aksiyon gerekmiyor.' };
  if (balance > 0 && (days == null || days > 20)) act = { tone: 'r', title: 'Tahsilat + Sipariş', text: `${formatTRY(balance)} açık bakiye${days != null ? ` · ${days} gün sipariş yok` : ''}` };
  else if (days != null && days > 30) act = { tone: 'r', title: 'Sipariş Ziyareti', text: `${days} gündür sipariş yok` };
  else if (balance > 0) act = { tone: 'a', title: 'Tahsilat', text: `${formatTRY(balance)} açık bakiye` };
  else if (days != null && days <= 7) act = { tone: 'g', title: 'Takip', text: `Son sipariş ${days} gün önce` };
  return (
    <>
      <div className="cd-info-section">
        <div className="cd-info-section-title">Önerilen Aksiyon</div>
        <div className={`cd-action cd-action--${act.tone}`}>
          <b>{act.title}</b>
          <span>{act.text}</span>
        </div>
      </div>
      <div className="cd-info-section">
        <div className="cd-info-section-title">Sipariş Özeti</div>
        <div className="cd-ministat">
          <div><b>{cnt}</b><span>Sipariş</span></div>
          <div><b>{formatTRY(total)}</b><span>Toplam</span></div>
          <div><b>{formatTRY(avg)}</b><span>Ortalama</span></div>
        </div>
        {lastRaw && <div className="cd-ministat-foot">Son sipariş · {orderDateLabel(lastRaw)}</div>}
      </div>
    </>
  );
}

export function CustomerDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [customer, setCustomer] = useState(null);
  const [groups, setGroups] = useState([]);
  const [subGroups, setSubGroups] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [orders, setOrders] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [topSkus, setTopSkus] = useState([]);
  const [topCampaigns, setTopCampaigns] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  // Menü aksiyon hakları — bu ekranda yalnız mevcut kayıt var (isNew yok):
  // Düzenle → canEdit, Sil → canDelete. Salt-okuma afordansları kısıtlanmaz.
  const { canEdit, canDelete } = usePageActions();

  usePageMeta('Müşteri Detayı', customer?.title || '');

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3500);
  };

  const fetchData = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    const headers = authHeaders();
    // Sipariş geçmişi: müşteri-bazlı endpoint yok → OData filtresiyle çekilir (guid literal tırnaksız).
    const ordersUrl = `/api/orderheaders?$filter=CustomerId eq ${id}&$orderby=DocumentDate desc&$top=20`;
    try {
      const [custRes, gRes, sgRes, vRes, oRes, cRes, skuRes, campRes] = await Promise.all([
        fetch(`/api/customers/GetById?Id=${encodeURIComponent(id)}`, { headers }),
        fetch('/api/customergroups/Select', { headers }),
        fetch('/api/customersubgroups/Select', { headers }),
        fetch('/api/vendors/Select', { headers }),
        fetch(ordersUrl, { headers }),
        fetch('/api/campaigns?take=500', { headers }),
        fetch(`/api/customers/top-skus?id=${encodeURIComponent(id)}&top=5`, { headers }),
        fetch(`/api/customers/top-campaigns?id=${encodeURIComponent(id)}&top=5`, { headers }),
      ]);
      if (!custRes.ok) throw new Error('Müşteri bulunamadı');
      const [custJson, gJson, sgJson, vJson, oJson, cJson, skuJson, campJson] = await Promise.all([
        custRes.json(),
        gRes.ok ? gRes.json() : [],
        sgRes.ok ? sgRes.json() : [],
        vRes.ok ? vRes.json() : [],
        oRes.ok ? oRes.json() : [],
        cRes.ok ? cRes.json() : [],
        skuRes.ok ? skuRes.json() : [],
        campRes.ok ? campRes.json() : [],
      ]);
      setCustomer(unwrapEntity(custJson));
      setGroups(unwrap(gJson));
      setSubGroups(unwrap(sgJson));
      setVendors(unwrap(vJson));
      setOrders(unwrap(oJson));
      setCampaigns(unwrapCampaigns(cJson));
      setTopSkus(unwrap(skuJson) || []);
      setTopCampaigns(unwrap(campJson) || []);
    } catch (e) {
      setError(e.message || 'Müşteri yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const getGroupLabel = useCallback((gid) => {
    if (!gid) return '-';
    const g = groups.find((x) => x.id === gid);
    return g ? (g.description || g.textId || '-') : '-';
  }, [groups]);

  const getSubGroupLabel = useCallback((sgid) => {
    if (!sgid) return '-';
    const g = subGroups.find((x) => x.id === sgid);
    return g ? (g.description || g.textId || '-') : '-';
  }, [subGroups]);

  const getVendorLabel = useCallback((vid) => {
    if (!vid) return '-';
    const v = vendors.find((x) => x.id === vid);
    if (!v) return '-';
    return v.name ? `${v.textId || ''} - ${v.name}`.replace(/^- /, '') : (v.textId || '-');
  }, [vendors]);

  const handleDelete = async () => {
    if (!customer) return;
    setDeleteConfirmOpen(false);
    try {
      const res = await fetch('/api/customers/Delete', {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ id: customer.id }),
      });
      if (!res.ok) throw new Error('Kayıt silinemedi');
      showToast('success', 'Müşteri silindi');
      setTimeout(() => navigate('/customers'), 600);
    } catch (e) {
      showToast('error', e.message || 'Silme işlemi başarısız');
    }
  };

  const statusMeta = customer ? STATUS_META[customer.status] : null;

  // Müşteriye uygulanabilir kampanyalar (istemci-çözümlü; Promo sekmesi + rozet sayısı).
  const applicableCampaigns = useMemo(
    () => (customer ? campaigns.filter((c) => campaignAppliesToCustomer(c, customer)) : []),
    [campaigns, customer],
  );

  // Sekme sayı rozetleri: yerel kayıt sayıları + uygulanabilir kampanya sayısı.
  // activeTab bağımlılığı, bir sekmede kayıt eklendikten sonra geçişte rozeti tazeler.
  const tabCounts = useMemo(() => {
    if (!customer) return {};
    return {
      collection: loadLocal(STORAGE_KEYS.payments, customer.id).length,
      survey: loadLocal(STORAGE_KEYS.surveys, customer.id).length,
      cooler: loadLocal(STORAGE_KEYS.coolerFaults, customer.id).length,
      standCount: loadLocal(STORAGE_KEYS.standCounts, customer.id).length,
      certificatedReturn: loadLocal(STORAGE_KEYS.returns, customer.id).length,
      depositCount: loadLocal(STORAGE_KEYS.deposits, customer.id).length,
      promo: applicableCampaigns.length,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer, applicableCampaigns, activeTab]);

  const tabsWithBadges = useMemo(
    () => TABS.map((t) => ({ ...t, badge: tabCounts[t.value] })),
    [tabCounts],
  );

  // Hızlı aksiyon hedefleri (boşsa buton pasif).
  const mapsQuery = customer ? [customer.adress, customer.city, customer.title].filter(Boolean).join(' ') : '';
  // Prototip lp-av baş harfleri (ünvandan; yoksa koddan).
  const customerInitials = (customer ? (customer.title || customer.textId || '?') : '?')
    .trim().split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join('').toUpperCase() || '?';

  return (
    <div style={s.container}>
      {loading && <div className="cd-loading">Yükleniyor...</div>}

      {error && !loading && (
        <div className="cd-error">{error}</div>
      )}

      {customer && !loading && !error && (
        <div className="cd-layout">
          <div className="cd-leftcol">
          <aside className="cd-sidebar">
            <div className="cd-sidebar-head">
              {/* Prototip lp-av: 80px yuvarlak, grad-navy zemin, ortalı baş harfler + soft halka */}
              <div className="cd-lp-av">{customerInitials}</div>
              <h2 className="cd-title">{customer.title || '-'}</h2>
              <div className="cd-id-row">
                {customer.textId && <span className="c360-erp-chip">{customer.textId}</span>}
                {statusMeta && <StatusBadge tone={statusMeta.tone}>{statusMeta.label}</StatusBadge>}
              </div>
              {/* Prototip icb: telefon / e-posta / konum ikon butonları (ortalı) */}
              <div className="cd-quick-actions">
                <a
                  className={`cd-quick-btn${customer.phone ? '' : ' is-disabled'}`}
                  href={customer.phone ? `tel:${String(customer.phone).replace(/\s+/g, '')}` : undefined}
                  aria-disabled={!customer.phone}
                  title={customer.phone ? `Ara: ${customer.phone}` : 'Telefon yok'}
                >
                  <CdIcon name="phone" />
                </a>
                <a
                  className={`cd-quick-btn${customer.email ? '' : ' is-disabled'}`}
                  href={customer.email ? `mailto:${customer.email}` : undefined}
                  aria-disabled={!customer.email}
                  title={customer.email ? `E-posta: ${customer.email}` : 'E-posta yok'}
                >
                  <CdIcon name="mail" />
                </a>
                <a
                  className={`cd-quick-btn${mapsQuery ? '' : ' is-disabled'}`}
                  href={mapsQuery ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(mapsQuery)}` : undefined}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-disabled={!mapsQuery}
                  title={mapsQuery ? 'Haritada göster' : 'Adres yok'}
                >
                  <CdIcon name="pin" />
                </a>
              </div>
            </div>

            <SidebarStats customer={customer} orders={orders} />

            <InfoSection title="İletişim">
              <InfoRow icon="user" label="Yetkili" value={customer.person} />
              <InfoRow icon="phone" label="Telefon" value={customer.phone} />
              <InfoRow icon="mail" label="E-posta" value={customer.email} />
            </InfoSection>

            <InfoSection title="Sınıflandırma">
              <InfoRow icon="tag" label="Kısa Ad" value={customer.shortName} />
              <InfoRow icon="users" label="Müşteri Grubu" value={getGroupLabel(customer.groupId)} />
              <InfoRow icon="folder" label="Ek Grup" value={getSubGroupLabel(customer.subGroupId)} />
              <InfoRow icon="truck" label="Distribütör" value={getVendorLabel(customer.vendorId)} />
            </InfoSection>

            <InfoSection title="Adres & Vergi">
              <InfoRow icon="building" label="Şehir" value={customer.city} />
              <InfoRow icon="pin" label="Adres" value={customer.adress} />
              <InfoRow icon="receipt" label="Vergi No" value={customer.taxNumber} />
              <InfoRow icon="bank" label="Vergi Dairesi" value={customer.taxOffice} />
              <InfoRow icon="receipt" label="E-Belge Mükellef Tipi" value={edocumentLabel(customer.eDocumentTaxpayerType)} />
              <InfoRow icon="truck" label="Varsayılan Sevkiyat Belge Tipi" value={customer.defaultShipmentDocumentType ? shipmentDocLabel(customer.defaultShipmentDocumentType) : null} />
            </InfoSection>

          </aside>

          <VisitSuggestion customer={customer} orders={orders} />
          <ProductSuggestion customer={customer} />
          </div>

          <main className="cd-main">
            {/* Prototip dbar: aksiyonlar sağa hizalı (Bilgiler / Düzenle / Sil) */}
            <div className="cd-dbar">
              <button type="button" className="cd-edit-btn cd-view-btn" onClick={() => navigate(`/customers/${id}/view`, { state: { from: `/customers/${id}` } })}>
                <CdIcon name="eye" /> Bilgiler
              </button>
              {canEdit && (
                <button type="button" className="cd-edit-btn" onClick={() => navigate(`/customers/${id}/edit`, { state: { row: customer, from: `/customers/${id}` } })}>
                  <CdIcon name="pencil" /> Düzenle
                </button>
              )}
              {canDelete && (
                <button type="button" className="cd-icon-btn cd-icon-btn--danger" onClick={() => setDeleteConfirmOpen(true)} title="Sil" aria-label="Sil">
                  <CdIcon name="trash" />
                </button>
              )}
            </div>
            <div className="ck-tabs" role="tablist" aria-label="Müşteri detayı sekmeleri">
              {tabsWithBadges.map((t) => (
                <button
                  key={t.value}
                  type="button"
                  role="tab"
                  aria-selected={activeTab === t.value}
                  className={'ck-tab ' + (activeTab === t.value ? 'is-on' : '')}
                  onClick={() => setActiveTab(t.value)}
                >
                  <CdIcon name={t.icon} size={16} /> {t.label}
                  {Number(t.badge) > 0 && <span className="ck-tabcount">{t.badge}</span>}
                </button>
              ))}
            </div>
            <div className="cd-tab-content">
              {activeTab === 'overview' && (
                <OverviewTab customer={customer} orders={orders} topSkus={topSkus} topCampaigns={topCampaigns} onOrderClick={(o) => navigate(`/orders/${pick(o, 'id', 'Id')}`)} />
              )}
              {activeTab === 'collection' && (
                <CollectionTab customer={customer} onToast={showToast} />
              )}
              {activeTab === 'promo' && (
                <PromoTab campaigns={applicableCampaigns} />
              )}
              {activeTab === 'survey' && (
                <SurveyTab customer={customer} onToast={showToast} />
              )}
              {activeTab === 'cooler' && (
                <CoolerTab customer={customer} onToast={showToast} />
              )}
              {activeTab === 'standCount' && (
                <StandCountTab customer={customer} onToast={showToast} />
              )}
              {activeTab === 'agreement' && (
                <AgreementTab customer={customer} />
              )}
              {activeTab === 'certificatedReturn' && (
                <CertificatedReturnTab customer={customer} onToast={showToast} />
              )}
              {activeTab === 'depositCount' && (
                <DepositCountTab customer={customer} onToast={showToast} />
              )}
            </div>
          </main>
        </div>
      )}

      {deleteConfirmOpen && customer && (
        <ConfirmDialog
          title="Silme Onayı"
          message={<p style={{ margin: 0 }}><strong>&quot;{customer.title}&quot;</strong> müşterisini silmek istediğinize emin misiniz?</p>}
          confirmLabel="Evet, Sil"
          tone="danger"
          onConfirm={handleDelete}
          onClose={() => setDeleteConfirmOpen(false)}
        />
      )}

      <Toast toast={toast} />
    </div>
  );
}
